/**
 * Loading Fallback Component
 * Professional loading states for lazy-loaded components
 * Maintains consistent UI design and branding
 */

import React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Skeleton,
  Paper
} from '@mui/material';

const LoadingFallback = ({ 
  variant = 'default', 
  message = 'Loading...', 
  showSkeleton = false,
  height = 'auto' 
}) => {
  // Default loading spinner
  if (variant === 'default') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: height === 'auto' ? '200px' : height,
          gap: 2,
          p: 3
        }}
      >
        <CircularProgress 
          size={40}
          sx={{ 
            color: 'var(--as-blue-500)',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round'
            }
          }}
        />
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            fontWeight: 500,
            opacity: 0.8
          }}
        >
          {message}
        </Typography>
      </Box>
    );
  }

  // Chat interface loading skeleton
  if (variant === 'chat') {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        {/* Sidebar skeleton */}
        <Box sx={{ width: '300px', borderRight: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
          </Box>
        </Box>
        
        {/* Main chat area skeleton */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Skeleton variant="text" width="40%" height={32} />
          </Box>
          
          {/* Messages area */}
          <Box sx={{ flex: 1, p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Skeleton variant="text" width="20%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={60} width="60%" />
            </Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Skeleton variant="rectangular" height={40} width="40%" />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Skeleton variant="text" width="20%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={80} width="70%" />
            </Box>
          </Box>
          
          {/* Input area */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Skeleton variant="rectangular" height={56} />
          </Box>
        </Box>
      </Box>
    );
  }

  // Profile page loading skeleton
  if (variant === 'profile') {
    return (
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="60%" height={24} />
        </Box>

        {/* Tabs */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Skeleton variant="rectangular" width={100} height={36} />
            <Skeleton variant="rectangular" width={120} height={36} />
            <Skeleton variant="rectangular" width={110} height={36} />
            <Skeleton variant="rectangular" width={80} height={36} />
            <Skeleton variant="rectangular" width={90} height={36} />
          </Box>
          <Skeleton variant="rectangular" height={1} />
        </Box>

        {/* Content area */}
        <Box sx={{ display: 'grid', gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Skeleton variant="text" width="25%" height={28} sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={56} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
                <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  // Minimal loading state
  if (variant === 'minimal') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <CircularProgress size={24} sx={{ color: 'var(--as-blue-500)' }} />
      </Box>
    );
  }

  // Inline loading for components
  if (variant === 'inline') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1
        }}
      >
        <CircularProgress size={16} sx={{ color: 'var(--as-blue-500)' }} />
        <Typography variant="caption" color="text.secondary">
          {message}
        </Typography>
      </Box>
    );
  }

  // Skeleton variant
  if (showSkeleton) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
    );
  }

  // Default fallback
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100px',
        color: 'text.secondary'
      }}
    >
      <Typography variant="body2">{message}</Typography>
    </Box>
  );
};

export default LoadingFallback; 